import * as Sentry from '@sentry/nextjs';
import { type GetStaticPaths, type GetStaticProps } from 'next';
import countryPageDataCanada from 'constants/countryPageDataCanada';
import countryPageDataMexico from 'constants/countryPageDataMexico';
import countryPageDataUsa from 'constants/countryPageDataUsa';
import {
  ACTIVE_COUNTRIES,
  ROOT_PAGE,
  getCountryIndexPageUrl,
  getCountrySubdivisionIndexPageUrl,
} from 'constants/pages';
import { SportsEnum } from 'types/generated/server';
import { generateBreadcrumbListJsonLd } from 'utils/shared/json-ld/breadcrumbs';
import { combineJsonLdSchemas } from 'utils/shared/json-ld/combineJsonLdSchemas';
import { generateFaqJsonLd } from 'utils/shared/json-ld/faq';
import { generatePlaceJsonLd } from 'utils/shared/json-ld/place';
import CitiesPage, { Props, generateBreadcrumbs } from 'screens/CitiesPage';

const REVALIDATE_IN_SECONDS = 7 * 24 * 60 * 60 * 1;

export const getStaticProps: GetStaticProps<Props, { countrySlug: string }> = async ({
  params,
}) => {
  try {
    const activeCountry = ACTIVE_COUNTRIES.find((country) => country.slug === params?.countrySlug);

    if (!activeCountry) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    const countryQuery =
      activeCountry.id === 'USA'
        ? countryPageDataUsa
        : activeCountry.id === 'CAN'
        ? countryPageDataCanada
        : activeCountry.id === 'MEX'
        ? countryPageDataMexico
        : null;

    const countryData = countryQuery?.data?.countriesByPk;

    if (!countryData) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    const subdivisionData = countryData.subdivisions || [];
    const activeSubdivisions = subdivisionData.filter((subdivision) => {
      const cities = subdivision.cities || [];
      const citiesWithCourts = cities.filter(
        (city) => (city?.venuesAggregate?.aggregate?.count || 0) > 0,
      );

      return !!citiesWithCourts && citiesWithCourts.length > 0;
    });

    /**
     * @note Mexico is failing here because we haven't activated subdivisions
     */
    if (!activeSubdivisions || activeSubdivisions.length === 0) {
      return {
        notFound: true,
        revalidate: REVALIDATE_IN_SECONDS,
      };
    }

    const subdivisions = (activeSubdivisions || []).map((subdivision) => {
      return {
        name: subdivision.name,
        slug: subdivision.slug,
        cities: subdivision.cities || [],
        courtCount: subdivision?.cities?.reduce(
          (acc, city) => acc + (city?.venuesAggregate?.aggregate?.count || 0),
          0,
        ),
      };
    });

    const countrySubdivisionPlaceJsonLd = generatePlaceJsonLd({
      name: countryData.name,
      geo: {
        latitude: countryData.latitude,
        longitude: countryData.longitude,
      },
      containsPlace: subdivisions.map((region) =>
        generatePlaceJsonLd({
          name: region.name,
          url: `${
            process.env.APP_URL
          }/${SportsEnum.Pickleball.toLowerCase()}${getCountrySubdivisionIndexPageUrl(
            region.slug,
          )}`,
        }),
      ),
    });
    const breadcrumbs = generateBreadcrumbListJsonLd({
      items: generateBreadcrumbs({
        countryName: activeCountry?.displayNameOverride || countryData.name,
        countrySlug: countryData.slug,
        sport: SportsEnum.Pickleball.toLowerCase(),
      }).jsonLd,
    });

    const combinedSchemas = combineJsonLdSchemas([breadcrumbs, countrySubdivisionPlaceJsonLd]);

    return {
      props: {
        countryName: activeCountry?.displayNameOverride || countryData.name,
        hasSubdivisionImages: activeCountry.hasSubdivisionImages || false,
        subdivisions: subdivisions || [],
        jsonLd: JSON.stringify(combinedSchemas),
        sport: SportsEnum.Pickleball.toLowerCase(),
      },
    };
  } catch (error) {
    console.log('--- ERROR: ', error);
    Sentry.captureException(error);
    return {
      notFound: true,
      revalidate: REVALIDATE_IN_SECONDS,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = ACTIVE_COUNTRIES.map((country) => ({
    params: {
      countrySlug: country.slug,
    },
  }));

  return {
    paths,
    fallback: true,
  };
};

export default CitiesPage;
